import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import styles from './UsernamePromptModal.module.css';

interface UsernamePromptModalProps {
  isOpen: boolean;
  onComplete: () => void;
  onCancel: () => void;
  isInitialSetup?: boolean;
}

export function UsernamePromptModal({ isOpen, onComplete, onCancel, isInitialSetup = false }: UsernamePromptModalProps) {
  const { setUsername, signOut } = useAuth();
  const [username, setUsernameValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Validate username (same rules as mobile)
  const validateUsername = (value: string): { valid: boolean; error?: string } => {
    if (!value || typeof value !== 'string') {
      return { valid: false, error: 'Username is required' };
    }

    const trimmed = value.trim();

    if (trimmed.length < 3) {
      return { valid: false, error: 'Username must be at least 3 characters' };
    }

    if (trimmed.length > 20) {
      return { valid: false, error: 'Username must be 20 characters or less' };
    }

    if (!/^[a-zA-Z0-9_ '-]+$/.test(trimmed)) {
      return { valid: false, error: 'Username can only contain letters, numbers, spaces, underscores, hyphens, and apostrophes' };
    }

    return { valid: true };
  };

  const handleSubmit = async () => {
    const validation = validateUsername(username);
    if (!validation.valid) {
      setError(validation.error || 'Invalid username');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await setUsername(username.trim());
      onComplete();
    } catch (err) {
      console.error('Error setting username:', err);
      setError(err instanceof Error ? err.message : 'Failed to set username');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (isInitialSetup) {
      const confirmed = window.confirm(
        'You need to set a username to continue. Cancel and sign out?'
      );
      if (confirmed) {
        await signOut();
        onCancel();
      }
    } else {
      onCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Set Your Username</h2>
          <button className={styles.closeButton} onClick={handleCancel}>
            &times;
          </button>
        </div>

        <div className={styles.content}>
          <p className={styles.description}>
            Choose a username to complete your account setup. This will be displayed
            on leaderboards and in your profile.
          </p>

          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="username">
              Username
            </label>
            <input
              id="username"
              type="text"
              className={styles.input}
              placeholder="Choose a username"
              value={username}
              onChange={(e) => {
                setUsernameValue(e.target.value);
                setError('');
              }}
              disabled={loading}
              maxLength={20}
              autoComplete="username"
              autoFocus
            />
            <span className={styles.hint}>
              3-20 characters
            </span>
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <button
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Please wait...' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
